/**
 * Parent category layer.
 *
 * The product DB only carries flat `category` slugs (e.g. "spices_whole").
 * We group these slugs into 19 customer-facing parent categories. This file
 * is the single source of truth for that grouping.
 *
 * Parents WITH at least one child slug having products = ACTIVE.
 * Parents with no products available yet are shown as greyed-out tiles
 * ("Coming soon") on the landing screen. We still render them so customers
 * know what's planned.
 *
 * Display order = the order in which they appear in PARENT_CATEGORIES below.
 */

export const PARENT_CATEGORIES = [
    // ─── Coming soon (no products yet) — kept at the top per the user's own list ordering
    { id: "dairy_bread_eggs",   label: "Dairy, bread and eggs",   icon: "🥛", children: [] },
    { id: "fruits_veg",         label: "Fruits and vegetables",    icon: "🥬", children: [] },
    { id: "beverages",          label: "Beverages",                icon: "🥤", children: [] },

    // ─── Active groups
    { id: "snacks_munchies",    label: "Snacks",                   icon: "🍿", children: ["snacks", "snacks_namkeen", "protien_snacks"] },
    { id: "rtc",                label: "RTC (Ready To Cook)",      icon: "🍳", children: ["rtc_ready_to_cook", "packed_food"] },
    { id: "rte",                label: "RTE (Ready To Eat)",       icon: "🍱", children: ["rte_ready_to_eat"] },
    { id: "noodles_pasta",      label: "Noodles & Pasta",          icon: "🍜", children: ["noodles_pasta"] },
    { id: "mouth_freshners",    label: "Mouth Freshners (Mukhwas)", icon: "🌿", children: ["mouth_freshners_mukhwas"] },
    { id: "sweet_tooth",        label: "Indian Sweets (Mithai)",   icon: "🍬", children: ["sweets", "indian_sweets_mithai"] },

    // ─── Coming soon
    { id: "bakery_biscuits",    label: "Bakery and biscuits",      icon: "🥖", children: [] },

    // ─── Active
    { id: "tea_coffee",         label: "Tea, coffee and milk drinks", icon: "🍵", children: ["tea"] },
    { id: "flour",              label: "Flour",                    icon: "🌾", children: ["flour"] },
    { id: "rice",               label: "Rice",                     icon: "🍚", children: ["rice"] },
    { id: "lentils_dal",        label: "Lentils (Dal)",            icon: "🫘", children: ["lentils_dal"] },
    { id: "spices_powder",      label: "Spices",                   icon: "🧂", children: ["spices_powder_masala"] },
    { id: "spices_whole",       label: "Whole Spices",             icon: "🌶️", children: ["spices_whole_sabut_masala"] },
    { id: "oil_ghee",           label: "Oil & Ghee",               icon: "🫙", children: ["oil_ghee"] },
    { id: "pickles",            label: "Pickles",                  icon: "🥒", children: ["pickles", "pickles_non_veg"] },
    { id: "condiment_and_sauce", label: "Condiment And Sauce",     icon: "🥫", children: ["condiment_and_sauce"] },

    // ─── Coming soon
    { id: "meat_fish",          label: "Chicken, meat and fish",   icon: "🍖", children: [] },

    // ─── Active
    { id: "organic_healthy",    label: "Organic and healthy living", icon: "🌱", children: ["coconut_products", "high_protien_food"] },

    // ─── Coming soon
    { id: "baby_care",          label: "Baby care",                icon: "🍼", children: [] },

    // ─── Active
    { id: "pharma_wellness",    label: "Pharma and wellness",      icon: "💊", children: ["health_care"] },

    // ─── Coming soon
    { id: "cleaning",           label: "Cleaning essentials",      icon: "🧽", children: [] },

    // ─── Active
    { id: "puja_needs",         label: "Puja Needs",               icon: "🪔", children: ["puja_needs", "fragrence_pooja_needs", "flavors_fragrance"] },
    { id: "home_office",        label: "Home and office",          icon: "🏠", children: [] },
    { id: "personal_care",      label: "Personal care",            icon: "🧴", children: ["body_care", "hair_care", "skin_care"] },

    // ─── Coming soon
    { id: "pet_care",           label: "Pet care",                 icon: "🐾", children: [] },
];

// Manual image overrides for parent tiles (user-supplied assets, wins over
// auto-derived product images so the storefront stays branded). Add an entry
// here whenever the merchant wants a specific landing-tile image.
const PARENT_IMAGE_OVERRIDES = {
    snacks_munchies: "https://customer-assets.emergentagent.com/job_bazaar-staging-1/artifacts/w8b0jl0d_Snacks.jpg",
    noodles_pasta:   "https://customer-assets.emergentagent.com/job_bazaar-staging-1/artifacts/da95j26n_Noodles%20%26%20pasta.png",
    sweet_tooth:     "https://customer-assets.emergentagent.com/job_bazaar-staging-1/artifacts/epnuyc77_Indian%20Sweets.png",
    spices_powder:   "https://customer-assets.emergentagent.com/job_bazaar-staging-1/artifacts/sicba6il_Spices.jpg",
    spices_whole:    "https://customer-assets.emergentagent.com/job_bazaar-staging-1/artifacts/g1gqbgqh_Whole%20Spices.jpg",
    lentils_dal:     "https://customer-assets.emergentagent.com/job_bazaar-staging-1/artifacts/u2gbz3qq_Lentils.jpg",
    rice:            "https://customer-assets.emergentagent.com/job_bazaar-staging-1/artifacts/f4m4u6y9_Rice.png",
    flour:           "https://customer-assets.emergentagent.com/job_bazaar-staging-1/artifacts/i2ttirrk_Flours.png",
    pickles:         "https://customer-assets.emergentagent.com/job_bazaar-staging-1/artifacts/9z9d5vjz_Pickles.png",
    oil_ghee:        "https://customer-assets.emergentagent.com/job_bazaar-staging-1/artifacts/mnyi8z7y_Oil%20%26%20Ghee.png",
    mouth_freshners: "https://customer-assets.emergentagent.com/job_bazaar-staging-1/artifacts/kma2ahla_Mouthfreshners.png",
    tea_coffee:      "https://customer-assets.emergentagent.com/job_bazaar-staging-1/artifacts/vy5zbfv2_Tea.png",
    rte:             "https://customer-assets.emergentagent.com/job_bazaar-staging-1/artifacts/dk8er0ox_RTE.png",
    rtc:             "https://customer-assets.emergentagent.com/job_bazaar-staging-1/artifacts/2bss5igw_RTC1.png",
    puja_needs:      "https://customer-assets.emergentagent.com/job_bazaar-staging-1/artifacts/8hr8lo14_Pooja%20Nees.png",
    personal_care:   "https://customer-assets.emergentagent.com/job_bazaar-staging-1/artifacts/u6yc2jns_Personal%20Care.png",
};

// Curated stock photos for parent categories that don't yet have products
// in the DB (so the "Coming soon" tiles still show a real image instead of
// a flat emoji). Unsplash URLs, sized small for fast mobile loading.
const PARENT_FALLBACK_IMAGES = {
    dairy_bread_eggs:   "https://images.unsplash.com/photo-1550583724-b2692b85b150?w=400&q=70&auto=format",
    fruits_veg:         "https://images.unsplash.com/photo-1610348725531-843dff563e2c?w=400&q=70&auto=format",
    beverages:          "https://images.unsplash.com/photo-1544145945-f90425340c7e?w=400&q=70&auto=format",
    bakery_biscuits:    "https://images.unsplash.com/photo-1509440159596-0249088772ff?w=400&q=70&auto=format",
    meat_fish:          "https://images.unsplash.com/photo-1607623814075-e51df1bdc82f?w=400&q=70&auto=format",
    organic_healthy:    "https://images.unsplash.com/photo-1542838132-92c53300491e?w=400&q=70&auto=format",
    baby_care:          "https://images.unsplash.com/photo-1515488042361-ee00e0ddd4e4?w=400&q=70&auto=format",
    cleaning:           "https://images.unsplash.com/photo-1583947215259-38e31be8751f?w=400&q=70&auto=format",
    pet_care:           "https://images.unsplash.com/photo-1583337130417-3346a1be7dee?w=400&q=70&auto=format",
};

// Reverse lookup: child slug → parent id
const _childToParent = (() => {
    const map = {};
    for (const p of PARENT_CATEGORIES) {
        for (const c of p.children) map[c] = p.id;
    }
    return map;
})();

export const parentOfSlug = (slug) => _childToParent[slug] || null;

/**
 * Combine raw API categories with the parent layer.
 * Returns: [{ id, label, name_key, icon, items, disabled, children: [api category objs] }]
 *
 * `items` is the rolled-up product count from the matching API categories.
 * `disabled` = true when no child has products available.
 */
export const buildParents = (apiCategories) => {
    const byId = {};
    for (const c of apiCategories || []) byId[c.id] = c;

    return PARENT_CATEGORIES.map((p) => {
        const matchedChildren = p.children
            .map((slug) => byId[slug])
            .filter(Boolean);
        const items = matchedChildren.reduce((s, c) => s + (c.items || 0), 0);
        // Image priority:
        //   1. Manual override (user-supplied asset)
        //   2. First child API category with a sample image (real product photo)
        //   3. Curated fallback (defined above) — used for coming-soon parents
        //   4. null → caller falls back to the emoji
        const sampleFromChildren = matchedChildren.find((c) => c.image)?.image || null;
        const image =
            PARENT_IMAGE_OVERRIDES[p.id]
            || sampleFromChildren
            || PARENT_FALLBACK_IMAGES[p.id]
            || null;
        return {
            id: p.id,
            label: p.label,
            name_key: `parent.${p.id}`,
            icon: p.icon,
            image,
            items,
            children: matchedChildren,        // resolved API category objs for sub-filter chips
            child_slugs: p.children,           // raw slugs (for matching products)
            disabled: items === 0,
        };
    });
};
